import { CommonModule } from "@angular/common";
import { Component, computed, inject, Signal, signal, TemplateRef, ViewChild } from "@angular/core";
import ScheduleDetailHeader from "../shared/schedule-detail-header/schedule-detail-header.component";
import { ActivatedRoute, Router } from "@angular/router";
import { AssertionIssueDTO } from "../../../../domains/jobIssues/models/IssueDTO";
import { Assertion, AssertionIssue, Component as JobComponent } from "../../../../core/database/db";
import { SkeletonModule } from "primeng/skeleton";
import { BadgeModule } from "primeng/badge";
import { ComponentConfigurationDTO } from "../../../../domains/areas/models/configuration/ComponentConfigurationDTO";
import { BottomSheetComponent } from "../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { PillToggleComponent, PillToggleOption } from "../../../../shared/components/pill-toggle/pill-toggle.component";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { LoggerService } from "../../../../shared/services/logger.service";
import { ButtonModule } from "primeng/button";
import { AreaInspectionSearchService } from "../../../../domains/areas/services/area-inspection-search.service";
import { InspectionItemSearchComponent, InspectionSearchItem } from "../../../../personas/ccm/BQ7/global-inspection-search/inspection-item-search.component";
import { BottomSheetStateService } from "../../../../shared/services/bottom-sheet-state.service";
import { BqStepDataServiceFactory } from "../../../../personas/shared/bq-step-data-service/bq-step-data-service-factory";
import { AssertionChanges, BqInspectionDataService } from "../../services/bq-inspection-data.service";
import { DummyBqInspectionDataService } from "../../services/dummy-bq-inspection-data-service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { AreaService } from "../../../areas/services/area.service";
import { InspectionJobDetail } from "../../models/InspectionJobDetail";
import { PreviousIssueCard, PreviousIssueContainerComponent } from "../previous-issue-container/previous-issue-container.component";
import { DataUpdatedFadeDirective } from "../../../../shared/directives/data-updated-fade.directive";

export enum FilterValue {
  All = "all",
  Reviewed = "reviewed",
  Pending = "pending",
  NeedsAttention = "needs-attention"
}

enum AssertionAction {
  LooksGood,
  NeedsAttention,
  NotApplicable
}

@Component({
  selector: "job-schedule-checklist",
  styleUrl: "job-schedule-checklist.component.scss",
  templateUrl: "job-schedule-checklist.component.html",
  standalone: true,
  imports: [
    CommonModule,
    ScheduleDetailHeader,
    SkeletonModule,
    ButtonModule,
    BadgeModule,
    BottomSheetComponent,
    PillToggleComponent,
    FormsModule,
    ReactiveFormsModule,
    InspectionItemSearchComponent,
    PreviousIssueContainerComponent,
    DataUpdatedFadeDirective
  ],
  providers: [BqStepDataServiceFactory]
})
export default class JobScheduleChecklist {

  constructor(private route: ActivatedRoute,
    private router: Router,
    private logger: LoggerService,
    private areaInspectionSearchService: AreaInspectionSearchService,
    private bqStepDataServiceFactory: BqStepDataServiceFactory,
    private areaService: AreaService,
    public bottomSheetState: BottomSheetStateService
  ) { }

  private toastService = inject(UiToastService);
  AssertionAction = AssertionAction;

  allComponents = signal<JobComponent[]>([]);
  assertions = signal<(Assertion & { componentName?: string })[]>([]);
  assertionsByComponent: { [ComponentId: number]: Signal<Assertion[]> } = {};
  filteredAssertionsByComponent: { [ComponentId: number]: Signal<Assertion[]> } = {};
  issuesByAssertion: { [AssertionIdentifier: string]: Signal<AssertionIssueDTO[]> } = {};
  activeComponents: Signal<JobComponent[]> = signal([]);
  issues = signal<AssertionIssueDTO[]>([]);
  reviewedAssertionsText: Signal<string> = signal("");
  pendingAssertionsCount: Signal<number> = signal(0);
  areaName: string = "";
  areaId = signal<number>(0);
  imageCounts = new Map<number, number>();
  updatingAssertionAction = signal<AssertionAction | null>(null);


  filters: PillToggleOption<FilterValue>[] = [
    { label: "Not Yet Reviewed", value: FilterValue.Pending },
    { label: "Reviewed", value: FilterValue.Reviewed },
    { label: "Needs Attention", value: FilterValue.NeedsAttention },
    { label: "All Items", value: FilterValue.All },
  ];

  activeFilter = signal<FilterValue>(FilterValue.Pending);

  dataLoaded: boolean = false;

  addingComponent: boolean = false;
  newComponentID: number = -1;

  currentAction = signal<string>("Viewing");

  @ViewChild("addComponentHeaderTemplate") addComponentHeaderTemplate?: TemplateRef<unknown>;
  @ViewChild("addComponentContentTemplate") addComponentContentTemplate?: TemplateRef<unknown>;

  newComponentForm: FormGroup = new FormGroup({
    selectedPreset: new FormControl(undefined, Validators.required),
  });

  processingAssertionId = signal<number | null>(null);
  searchVisible = signal(false);
  // searchableInspectionItems = signal<InspectionSearchItem[]>([]);
  targetAssertionIdentifier = signal<string | null>(null);
  spotlightAssertionIdentifier = signal<string | null>(null);

  // Carryover stepper for issues from the preceding BQ step (e.g. BQ6 -> BQ7); only used during a CCM inspection.
  previousIssueContainerOpen = signal<boolean>(false);
  previousIssueContainerAssertion = signal<Assertion | null>(null);
  previousIssueContainerIssues = signal<PreviousIssueCard[]>([]);
  previousIssueContainerAssertionText = computed(() => this.previousIssueContainerAssertion()?.text ?? "");
  /** Assertion whose card briefly fades out after a carryover save, for visual feedback. */
  carryoverFadingAssertionId = signal<number | null>(null);
  private spotlightTimeoutId?: ReturnType<typeof setTimeout>;
  private bqStepDataService = signal<BqInspectionDataService>(new DummyBqInspectionDataService(this.toastService));

  dataServiceJob = computed(() => this.bqStepDataService()?.job() ?? (() => {
    throw new Error("Job data not available in BqInspectionDataService");
  })());
  dataServiceLot = computed(() => this.bqStepDataService()?.lot() ?? (() => {
    throw new Error("Lot data not available in BqInspectionDataService");
  })());
  dataServiceComponents = computed(() => this.bqStepDataService()?.components() ?? []);
  dataServiceAreas = computed(() => this.bqStepDataService()?.areas() ?? []);
  dataServiceAssertions = computed(() => this.bqStepDataService()?.assertions() ?? []);
  dataServiceIssues = computed(() => this.bqStepDataService()?.assertionIssues() ?? []);
  dataServiceMedia = computed(() => this.bqStepDataService()?.media() ?? []);
  dataServiceAreasConfig = computed(() => this.bqStepDataService()?.areasConfig() ?? []);
  dataServiceZones = computed(() => this.bqStepDataService()?.zones() ?? []);
  jobId = computed(() => this.dataServiceJob().id);
  isCcmInspection = computed(() => this.bqStepDataService()?.isCcmInspection() ?? false);
  isRefreshingInBackground = computed(() => this.bqStepDataService()?.isRefreshingInBackground() ?? false);
  dataUpdatedPulse = computed(() => this.bqStepDataService()?.dataUpdatedPulse() ?? false);
  inspectionJob = computed<InspectionJobDetail>(() => ({
    ...this.dataServiceLot(),
    ...this.dataServiceJob(),
    streetAddress: this.dataServiceJob().streetAddress ?? this.dataServiceLot().streetAddress,
  }));
  componentPresets = computed(() =>
    this.dataServiceAreasConfig()
      .flatMap(area => area.components)
      .reduce<ComponentConfigurationDTO[]>((presets, component) => {
        if (!presets.some(preset => preset.name === component.name)) {
          presets.push(component);
        }
        return presets;
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name))
  );
  areasWithDetails = computed(() => {
    const areas = this.dataServiceAreas();
    const components = this.dataServiceComponents();
    const assertions = this.dataServiceAssertions();
    const assertionIssues = this.dataServiceIssues();
    const zones = this.dataServiceZones();
    const isCcmInspection = this.isCcmInspection();

    const areasWithDetails = areas.map(area => this.areaService.createAreaWithDetails(zones, area, components, assertions, assertionIssues, isCcmInspection));
    return areasWithDetails;
  });
  searchableInspectionItems = computed<InspectionSearchItem[]>(() => this.areaInspectionSearchService.buildFromAreaDetails(this.areasWithDetails()));

  async ngOnInit(): Promise<void> {
    const filter = this.route.snapshot.queryParamMap.get("filter") as FilterValue;
    const bqStep = this.route.snapshot.parent?.data["bqStep"] as string;
    this.bqStepDataService.set(this.bqStepDataServiceFactory.getService<BqInspectionDataService>(bqStep));
    this.activeFilter.set(filter ?? FilterValue.Pending);

    this.route.paramMap.subscribe({
      next: async params => {
        this.dataLoaded = false;

        this.areaId.set(parseInt(params.get("areaId") ?? "0"));
        await this.initializeComponent();

        this.dataLoaded = true;
        this.scrollToRequestedAssertion();
      },
      error: err => {
        this.toastService.error("Error loading job data. Please try again.");
        this.logger.error(err);
        this.goBack();
      }
    });

    this.route.queryParamMap.subscribe({
      next: params => {
        const spotlightRequested = params.get("spotlight") === "true";
        const requestedAssertionIdentifier = spotlightRequested ? params.get("assertionIdentifier") : null;
        this.targetAssertionIdentifier.set(requestedAssertionIdentifier);
        if (this.dataLoaded && requestedAssertionIdentifier) {
          this.scrollToRequestedAssertion();
        }
      },
      error: err => {
        this.toastService.error("Error processing query parameters. Please try again.");
        this.logger.error(err);
        this.goBack();
      }
    });
  }

  async initializeComponent() {
    this.allComponents.set(
      this.dataServiceComponents()
        .filter(c => c.areaId === this.areaId())
        .map(comp => ({
          id: comp.id!,
          name: comp.name,
          lotId: comp.lotId,
          identifier: comp.identifier,
          updatedAt: comp.updatedAt,
          syncStatus: comp.syncStatus,
          areaId: comp.areaId,
          assertions: []
        }))
    );

    this.assertions.set(
      this.dataServiceAssertions()
        .filter(a => this.allComponents().some(c => c.id === a.componentId))
        .map(a => {
          const component = this.allComponents().find(c => c.id === a.componentId);
          return {
            ...a,
            issues: [],
            componentName: component?.name
          };
        })
    );

    this.syncIssuesFromDataService();

    for (const component of this.allComponents()) {
      const componentId = component.id!;
      this.assertionsByComponent[componentId] = computed(() => this.assertions().filter(a => a.componentId === componentId));
      if (!this.assertionsByComponent[componentId]() || this.assertionsByComponent[componentId]().length === 0) {
        this.logger.error(`No assertions found for component ID ${componentId} with identifier ${component.identifier} and name ${component.name} for job ID ${this.jobId()} and area ID ${this.areaId()}`);
      }

      for (const assertionIdentifier of this.assertionsByComponent[componentId]().map(a => a.identifier)) {
        this.issuesByAssertion[assertionIdentifier] = computed(() => this.issues().filter(i => i.assertionIdentifier === assertionIdentifier));
      }

      this.filteredAssertionsByComponent[componentId] = computed(() =>
        this.filteredAssertionsByComponentFn(componentId)
      );
    }

    this.activeComponents = computed(() => this.activeComponentsFn());
    for (const assertion of this.assertions()) {
      const count = this.bqStepDataService()
        .getLooksGoodMediaForAssertion(assertion.identifier).length;
      this.imageCounts.set(assertion.id!, count);
    }
    this.pendingAssertionsCount = computed(() => this.pendingAssertionsCountFn());

    this.reviewedAssertionsText = computed(() => this.reviewedAssertionsTextFn());
    this.areaName = this.dataServiceAreas().find(area => area.id === this.areaId())?.name ?? "";
    this.reviewedAssertionsText();
  }

  activeComponentsFn() {
    let components: JobComponent[] = [];
    switch (this.activeFilter()) {
      case "all":
        components = this.allComponents().filter(c => {
          if (this.filteredAssertionsByComponent[c.id!]) {
            return this.filteredAssertionsByComponent[c.id!]().length > 0;
          }

          this.logger.error(`case = all, No filteredAssertionsByComponent signal for component ID ${c.id} with identifier ${c.identifier} and name ${c.name} for job ID ${this.jobId()} and area ID ${this.areaId()}`);
          return false;
        });
        break;
      case "pending":
        components = this.allComponents().filter(c => {
          if (this.filteredAssertionsByComponent[c.id!]) {
            return this.filteredAssertionsByComponent[c.id!]().filter(
              a =>
                !(this.isCcmInspection() ? a.isCcmComplete : a.isComplete) && a.isApplicable && this.issuesByAssertion[a.identifier]().length === 0
            ).length > 0;
          }

          this.logger.error(`case = pending, No filteredAssertionsByComponent signal for component ID ${c.id} with identifier ${c.identifier} and name ${c.name} for job ID ${this.jobId()} and area ID ${this.areaId()}`);
          return false;
        });
        break;
      case "reviewed":
        components = this.allComponents().filter(c => {
          if (this.filteredAssertionsByComponent[c.id!]) {
            return this.filteredAssertionsByComponent[c.id!]().filter(
              a =>
                ((this.isCcmInspection() ? a.isCcmComplete : a.isComplete) || !a.isApplicable || this.issuesByAssertion[a.identifier]().length > 0)
            ).length > 0;
          }

          this.logger.error(`case = reviewed, No filteredAssertionsByComponent signal for component ID ${c.id} with identifier ${c.identifier} and name ${c.name} for job ID ${this.jobId()} and area ID ${this.areaId()}`);
          return false;
        });
        break;
      case "needs-attention":
        components = this.allComponents().filter(c => {
          if (this.filteredAssertionsByComponent[c.id!]) {
            return this.filteredAssertionsByComponent[c.id!]().filter(
              a =>
                (!a.isApplicable || this.issuesByAssertion[a.identifier]().length > 0)
            ).length > 0;
          }

          this.logger.error(`case = needs-attention, No filteredAssertionsByComponent signal for component ID ${c.id} with identifier ${c.identifier} and name ${c.name} for job ID ${this.jobId()} and area ID ${this.areaId()}`);
          return false;
        });
        break;
      default:
        components = this.allComponents().filter(
          c => {
            if (this.filteredAssertionsByComponent[c.id!]) {
              return this.filteredAssertionsByComponent[c.id!]().length > 0;
            }

            this.logger.error(`default case, No filteredAssertionsByComponent signal for component ID ${c.id} with identifier ${c.identifier} and name ${c.name} for job ID ${this.jobId()} and area ID ${this.areaId()}`);
            return false;
          });
        break;
    }

    const activeComponents = components.sort((a, b) => a.name.localeCompare(b.name));

    return activeComponents;
  }

  assertionsByComponentFn(componentId: number) {
    const assertions = this.assertions().filter(a => a.componentId === componentId);
    if (!assertions || assertions.length === 0) {
      this.logger.error(`No assertions found for component ID ${componentId} for job ID ${this.jobId()} and area ID ${this.areaId()}`);
    }
    return assertions;
  }

  filteredAssertionsByComponentFn(componentId: number) {
    let assertions: Assertion[] = [];
    switch (this.activeFilter()) {
      case "all":
        assertions = this.assertionsByComponent[componentId]();
        break;
      case "pending":
        assertions = this.assertionsByComponent[componentId]().filter(
          a =>
            !(this.isCcmInspection() ? a.isCcmComplete : a.isComplete)
            && a.isApplicable
            && this.issuesByAssertion[a.identifier]().length === 0
        );
        break;
      case "reviewed":
        assertions = this.assertionsByComponent[componentId]().filter(
          a =>
            ((this.isCcmInspection() ? a.isCcmComplete : a.isComplete) || !a.isApplicable || this.issuesByAssertion[a.identifier]().length > 0)
        );
        break;
      case "needs-attention":
        assertions = this.assertionsByComponent[componentId]().filter(
          a =>
            (this.issuesByAssertion[a.identifier]().length > 0)
        );
        break;
      default:
        assertions = this.assertionsByComponent[componentId]().filter(a =>
          a.isApplicable);
        break;
    }

    const filteredAssertionsByComponent = assertions.sort((a, b) => a.text.localeCompare(b.text));

    return filteredAssertionsByComponent;
  }

  pendingAssertionsCountFn() {
    return this.assertions().filter(
      assertion =>
        !(this.isCcmInspection() ? assertion.isCcmComplete : assertion.isComplete)
        && assertion.isApplicable
        && this.issuesByAssertion[assertion.identifier]
        && this.issuesByAssertion[assertion.identifier]().length === 0
    ).length;
  }

  reviewedAssertionsTextFn() {
    return `${this.assertions().length - this.pendingAssertionsCount()}/${this.assertions().length}`;
  }
  setActiveFilter(filterValue: FilterValue) {
    this.activeFilter.set(filterValue);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { filter: filterValue },
      replaceUrl: true
    });
  }

  isSpotlightedAssertion(assertion: Assertion): boolean {
    return this.spotlightAssertionIdentifier() === assertion.identifier;
  }

  private scrollToRequestedAssertion(): void {
    const assertionIdentifier = this.targetAssertionIdentifier();

    if (!assertionIdentifier) {
      return;
    }

    this.activeFilter.set(FilterValue.All);
    this.spotlightAssertionIdentifier.set(assertionIdentifier);

    setTimeout(() => {
      document.getElementById(`assertion-${assertionIdentifier}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    if (this.spotlightTimeoutId) {
      clearTimeout(this.spotlightTimeoutId);
    }

    this.spotlightTimeoutId = setTimeout(() => {
      this.spotlightAssertionIdentifier.set(null);
    }, 2500);
  }

  openSearch(): void {
    this.searchVisible.set(true);

    setTimeout(() => {
      document.getElementById("inspection-search-input")?.focus();
    });
  }

  closeSearch(): void {
    this.searchVisible.set(false);
  }

  navigateToSearchResult(result: InspectionSearchItem): void {
    this.activeFilter.set(FilterValue.All);
    this.closeSearch();

    this.router.navigate(["../../area", result.areaId], {
      relativeTo: this.route,
      queryParams: {
        assertionIdentifier: result.assertionIdentifier,
        spotlight: true,
      },
      fragment: `assertion-${result.assertionIdentifier}`,
    });
  }

  goBack() {
    this.router.navigate(["overview", "locations"], {
      relativeTo: this.route.parent
    });
  }

  openAddComponentDialog() {
    this.addingComponent = true;
  }
  closeAddComponentDialog() {
    this.addingComponent = false;
    this.newComponentForm.reset();
  }

  onAddComponentCancel() {
    this.closeAddComponentDialog();
  }

  onAddComponentSubmit() {
    if (this.newComponentForm.valid) {
      this.addComponent(this.newComponentForm.value.selectedPreset);
    }
  }

  selectComponent(index: number) {
    this.newComponentForm.setValue({
      selectedPreset: index
    });
  }


  async addComponent(index: number) {
    const newComponent: ComponentConfigurationDTO | undefined = this.componentPresets()[index];

    if (!newComponent) {
      this.toastService.error("Error", "No component preset found for the selected index.");
      this.logger.error(`[JobScheduleChecklist:addComponent] No component preset found for index ${index}.`);
      this.closeAddComponentDialog();
      return;
    }

    const { addedComponent, addedAssertions } = await this.bqStepDataService().addNewComponentToArea(newComponent, this.areaId());

    this.activeFilter.set(FilterValue.Pending);

    // We have to update all relevant/base signals so that component behaves as expected
    addedAssertions.forEach(a => {
      this.issuesByAssertion[a!.identifier] = signal([]);
    });
    this.assertions.update(assertions =>
      [...assertions, ...addedAssertions]
    );
    this.allComponents.update(components => [...components, addedComponent!]);
    this.assertionsByComponent[addedComponent.id!] = computed(() =>
      this.assertionsByComponentFn(addedComponent.id!)
    );
    this.filteredAssertionsByComponent[addedComponent.id!] = computed(() =>
      this.filteredAssertionsByComponentFn(addedComponent.id!)
    );
    this.pendingAssertionsCount = computed(() =>
      this.pendingAssertionsCountFn()
    );
    this.reviewedAssertionsText = computed(() =>
      this.reviewedAssertionsTextFn()
    );
    this.closeAddComponentDialog();

    // add, then remove class for new area added animation (blue border)
    setTimeout(() => {
      const el = document.getElementById(`component-${addedComponent.id!}`);
      el!.scrollIntoView({ behavior: "smooth", block: "start" });
      this.newComponentID = addedComponent.id!;
      setTimeout(() => {
        this.newComponentID = -1;
      }, 2500);
    }, 1);
  }

  async setLooksGood(assertion: Assertion) {
    const changes: AssertionChanges = {
      isApplicable: true,
    };

    if (this.isCcmInspection()) {
      changes.isCcmComplete = true;
      changes.ccmNote = ""; // Clear out any old notes if they previously marked it as an issue
    } else {
      changes.isComplete = true;
      changes.note = ""; // Clear out any old notes if they previously marked it as an issue
    }

    await this.updateAssertionState(assertion, changes, AssertionAction.LooksGood);
  }

  async addIssue(assertion: Assertion) {
    if (!assertion.id) {
      this.logger.warn("Cannot update an assertion without an ID!");
      return;
    }

    const timeout = window.setTimeout(() => {
      this.updatingAssertionAction.set(AssertionAction.NeedsAttention);
    }, 750);

    // During a CCM audit, clicking the Issue button on an assertion that still has open BQ6 (CM
    // self-inspection) findings opens the carryover stepper instead of jumping straight to a blank issue.
    if (this.isCcmInspection() && this.getPreviousIssueCount(assertion) > 0) {
      this.openPreviousIssueContainer(assertion);
      return;
    }

    await this.navigateToNeedsAttention(assertion);

    clearTimeout(timeout);
    this.updatingAssertionAction.set(null);
  }

  private async navigateToNeedsAttention(assertion: Assertion): Promise<void> {
    if (!assertion.id) {
      this.logger.warn("Cannot update an assertion without an ID!");
      return;
    }

    this.processingAssertionId.set(assertion.id);

    try {

      await this.bqStepDataService().startJob();

      await this.router.navigate(["component", assertion.componentId, "assertion", assertion.id, "needs-attention"],
        {
          relativeTo: this.route,
          queryParams: { filter: this.activeFilter() },
        }
      );
    } finally {
      this.processingAssertionId.set(null);
    }
  }

  /** Number of still-open issues carried into the current step for the assertion (drives the badge). */
  getPreviousIssueCount(assertion: Assertion): number {
    return this.bqStepDataService().getOpenCarryoverIssuesForAssertion(assertion.identifier).length;
  }

  openPreviousIssueContainer(assertion: Assertion): void {
    const openIssues = this.bqStepDataService().getOpenCarryoverIssuesForAssertion(assertion.identifier);

    const cards: PreviousIssueCard[] = openIssues.map(issue => ({
      issue,
      media: this.bqStepDataService().getCarryoverMediaForIssue(issue.identifier)
    }));

    this.previousIssueContainerAssertion.set(assertion);
    this.previousIssueContainerIssues.set(cards);
    this.previousIssueContainerOpen.set(true);
  }

  async onPreviousIssuesConfirmed(confirmations: { issue: AssertionIssue; severity: string }[]): Promise<void> {
    // The stepper handles a single assertion. It only drops off the list on the "Not Yet Reviewed"
    // filter, so only there do we hold + fade its card for feedback (elsewhere it just changes look).
    const assertionId = this.previousIssueContainerAssertion()?.id ?? null;
    const willDisappear = assertionId !== null && this.activeFilter() === FilterValue.Pending;

    if (willDisappear) {
      this.processingAssertionId.set(assertionId);
    }

    try {
      for (const confirmation of confirmations) {
        await this.bqStepDataService().confirmCarryoverIssueToCurrentStep(confirmation.issue, confirmation.severity);
      }

      // Once the stepper has closed, hold the card in place for a beat and then fade it out so the CCM
      // sees which item was handled before it leaves the reactive list (mirrors "Looks Good").
      if (willDisappear) {
        await new Promise(resolve => setTimeout(resolve, 400));
        this.carryoverFadingAssertionId.set(assertionId);
        await new Promise(resolve => setTimeout(resolve, 250));
      }

      // Reflect the newly created BQ7 issues so the assertion moves into "Reviewed" / "Needs Attention"
      // without a page reload.
      this.syncIssuesFromDataService();
    } finally {
      this.carryoverFadingAssertionId.set(null);
      this.processingAssertionId.set(null);
    }
  }

  /**
   * Re-snapshots the current area's assertion issues from the data service into the local `issues` signal
   * so the reactive filter/status computeds reflect changes made after the initial load (e.g. BQ6 carryover
   * issues confirmed into BQ7).
   */
  private syncIssuesFromDataService(): void {
    this.issues.set(
      this.dataServiceIssues()
        .filter(issue =>
          this.assertions().some(a => a.identifier === issue.assertionIdentifier) &&
          issue.deleted !== 1
        )
        .map(q => ({
          ...q,
          deleted: q.deleted === 1,
          jobIssueVendors: []
        }))
    );
  }

  onPreviousIssuesDismissed(issues: AssertionIssue[]): void {
    this.bqStepDataService().dismissCarryoverIssues(issues.map(issue => issue.identifier));
  }

  onPreviousIssuesAddNew(): void {
    const assertion = this.previousIssueContainerAssertion();
    this.previousIssueContainerOpen.set(false);

    if (assertion) {
      this.navigateToNeedsAttention(assertion);
    }
  }

  async removeNA(assertion: Assertion) {
    const changes: AssertionChanges = {
      isApplicable: true,
    };

    if (this.isCcmInspection()) {
      changes.isCcmComplete = false;
      changes.ccmNote = "";
    } else {
      changes.isComplete = false;
      changes.note = "";
    }

    await this.updateAssertionState(assertion, changes, AssertionAction.NotApplicable);
  }

  async markAsNA(assertion: Assertion) {
    const changes: AssertionChanges = {
      isApplicable: false,
    };

    if (this.isCcmInspection()) {
      changes.isCcmComplete = false;
      changes.ccmNote = "";
    } else {
      changes.isComplete = false;
      changes.note = "";
    }

    await this.updateAssertionState(assertion, changes, AssertionAction.NotApplicable);
  }

  navToLooksGoodDetails(assertion: Assertion) {
    setTimeout(() => {
      this.router.navigate(
        ["component", assertion.componentId, "assertion", assertion.id, "details"],
        {
          relativeTo: this.route,
          queryParams: { filter: this.activeFilter() },
        });
    }, 120);
  }
  navToIssueList(assertion: Assertion) {
    setTimeout(() => {
      this.router.navigate(["component", assertion.componentId, "assertion", assertion.id, "issues"], {
        relativeTo: this.route,
        queryParams: { filter: this.activeFilter() }
      });
    }, 120);
  }

  getAssertionStatus(assertion: Assertion): "completed" | "needsAttention" | "notCompleted" | "na" {
    if (!assertion.isApplicable) {
      return "na";
    }

    const isCompleted = (this.isCcmInspection() ? assertion.isCcmComplete : assertion.isComplete);
    if (isCompleted) {
      return "completed";
    }

    if (this.issuesByAssertion[assertion.identifier]().length > 0) {
      return "needsAttention";
    }

    return "notCompleted";
  }

  isAssertionReviewed(assertion: Assertion): boolean {
    if (!assertion) {
      return false;
    }

    const isCompleted = (this.isCcmInspection() ? assertion.isCcmComplete : assertion.isComplete);
    return (isCompleted)
      || (!assertion.isApplicable)
      || (this.issuesByAssertion[assertion.identifier]().length > 0);
  }

  private async updateAssertionState(assertion: Assertion, changes: AssertionChanges, action: AssertionAction): Promise<void> {
    // Safety check: Never try to update a record that doesn't exist in the DB yet
    if (!assertion.id) {
      this.logger.warn("Cannot update an assertion without an ID!");
      this.toastService.error("Update failed: Assertion does not exist in the database.");
      this.goBack();
      return;
    }

    this.processingAssertionId.set(assertion.id);
    const timeout = window.setTimeout(() => {
      this.updatingAssertionAction.set(action);
    }, 750);

    try {
      await this.bqStepDataService().startJob();
      const updatedAssertion = await this.bqStepDataService().updateAssertionState(assertion, changes);

      const assertions = this.assertions();
      const index = assertions.findIndex(a => a.id === assertion.id);

      if (index !== -1) {
        assertions[index] = { ...assertions[index], ...updatedAssertion };
        this.assertions.set([...assertions]);
      }

    } finally {
      if (timeout) {
        clearTimeout(timeout);
      }
      this.updatingAssertionAction.set(null);
      this.processingAssertionId.set(null);
    }
  }
}
